/**
 * Haptic Feedback Utility
 * 
 * Provides nuanced vibration patterns with configurable intensity
 * and respect for reduced motion preferences.
 * 
 * @module hapticFeedback
 */

import { logger } from './logger';

/**
 * Haptic vibration patterns
 */
export const HapticPatterns = {
  CORRECT: [50, 30, 50], // Short, pause, short
  INCORRECT: [100],
  COMPLETE: [50, 30, 50, 30, 50],
  ACHIEVEMENT: [100, 50, 100, 50, 200],
  BUTTON_CLICK: [10],
  ERROR: [200],
  SUCCESS: [50, 30, 50],
  WARNING: [100, 50, 100]
};

/**
 * Check if haptic feedback is supported
 * 
 * @returns {boolean} Support status
 */
export const isHapticSupported = () => {
  return 'vibrate' in navigator;
};

/**
 * Check if reduced motion is preferred
 * 
 * @returns {boolean} Reduced motion preference
 */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Trigger haptic feedback
 * 
 * @param {number[]|string} pattern - Vibration pattern array or pattern name
 * @param {number} [intensity=1.0] - Intensity multiplier (0.0 to 2.0)
 * @returns {boolean} Success status
 * 
 * @example
 * triggerHaptic(HapticPatterns.CORRECT, 1.5);
 * triggerHaptic('correct', 0.8);
 */
export const triggerHaptic = (pattern, intensity = 1.0) => {
  // Check support
  if (!isHapticSupported()) {
    return false;
  }

  // Respect reduced motion preference
  if (prefersReducedMotion()) {
    return false;
  }

  try {
    // Get pattern array
    let patternArray = pattern;
    if (typeof pattern === 'string') {
      patternArray = HapticPatterns[pattern.toUpperCase()] || HapticPatterns.BUTTON_CLICK;
    }

    // Validate pattern
    if (!Array.isArray(patternArray) || patternArray.length === 0) {
      // Lazy load logger to avoid initialization issues
      import('./logger').then(({ logger }) => {
        logger.warn('Invalid haptic pattern:', pattern);
      }).catch(() => {});
      return false;
    }

    // Apply intensity (clamp between 0 and 2)
    const clampedIntensity = Math.max(0, Math.min(2, intensity));
    const adjustedPattern = patternArray.map(duration => 
      Math.round(duration * clampedIntensity)
    );

    // Trigger vibration
    navigator.vibrate(adjustedPattern);
    return true;
  } catch (error) {
    // Lazy load logger to avoid initialization issues
    import('./logger').then(({ logger }) => {
      logger.error('Failed to trigger haptic feedback:', error);
    }).catch(() => {});
    return false;
  }
};

/**
 * Create custom haptic pattern
 * 
 * @param {number[]} durations - Array of vibration durations in milliseconds
 * @returns {number[]} Pattern array
 */
export const createPattern = (durations) => {
  return durations;
};

/**
 * Stop all vibrations
 */
export const stopHaptic = () => {
  if (isHapticSupported()) {
    try {
      navigator.vibrate(0);
    } catch (error) {
      // Lazy load logger to avoid initialization issues
      import('./logger').then(({ logger }) => {
        logger.error('Failed to stop haptic feedback:', error);
      }).catch(() => {});
    }
  }
};

